import React, { useState, useEffect, useRef } from 'react';
import { X, GripVertical } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Condo, OpenTab } from '../types';

interface CustomerTabsProps {
  openTabs: OpenTab[];
  activeTabId: string | null;
  setActiveTabId: (id: string) => void;
  allCondos: Condo[];
  setCloseTabConfirmation: (confirmation: { tabName: string; index: number; position: { x: number; y: number } } | null) => void;
  onCloseTab?: (customerId: string) => void;
  getUnreadCount?: (customerId: string) => number;
  onReorderTabs?: (tabs: OpenTab[]) => void;
}

const CustomerTabsComponent: React.FC<CustomerTabsProps> = ({
  openTabs,
  activeTabId,
  setActiveTabId,
  allCondos,
  setCloseTabConfirmation,
  onCloseTab,
  getUnreadCount,
  onReorderTabs,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const tabsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Keyboard shortcuts: Ctrl/Cmd + 1-9 to switch tabs
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl (Windows/Linux) or Cmd (Mac) + number
      if ((e.ctrlKey || e.metaKey) && e.key >= '1' && e.key <= '9') {
        const tabIndex = parseInt(e.key) - 1;
        if (tabIndex < openTabs.length) {
          e.preventDefault();
          setActiveTabId(openTabs[tabIndex].customerId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openTabs, setActiveTabId]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());

    // Add a slight delay to show the drag effect
    const target = e.currentTarget as HTMLElement;
    setTimeout(() => {
      target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder tabs
    const newTabs = [...openTabs];
    const [draggedTab] = newTabs.splice(draggedIndex, 1);
    newTabs.splice(dropIndex, 0, draggedTab);

    if (onReorderTabs) {
      onReorderTabs(newTabs);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  if (openTabs.length === 0) {
    return (
      <div className="px-6 py-3 bg-black border-b border-gray-900">
        <div className="text-center text-gray-600 text-xs">
          Selecione um cliente para começar
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-2 bg-black border-b border-gray-900">
      <div className="flex items-center gap-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {openTabs.map((tab, index) => {
          const condo = allCondos.find(c => c.id === tab.customerId);
          const isActive = activeTabId === tab.customerId;
          const unreadCount = getUnreadCount ? getUnreadCount(tab.customerId) : 0;
          const isDragging = draggedIndex === index;
          const isDragOver = dragOverIndex === index;
          const showNumber = index < 9; // Only show numbers 1-9

          return (
            <div
              key={tab.customerId}
              ref={(el) => { tabsRef.current[index] = el; }}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={cn(
                "group flex items-center gap-1.5 px-2 py-2 rounded-t-lg border-t border-l border-r transition-all flex-shrink-0 whitespace-nowrap text-xs relative cursor-grab active:cursor-grabbing",
                isActive
                  ? "bg-gray-950 border-gray-800 text-gray-200"
                  : "bg-black border-gray-900 text-gray-500 hover:bg-gray-950 hover:text-gray-400",
                isDragging && "opacity-50",
                isDragOver && "border-blue-500 border-2"
              )}
            >
              {/* Drag handle */}
              <GripVertical className="h-3 w-3 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />

              {/* Tab number badge */}
              {showNumber && (
                <span className={cn(
                  "flex items-center justify-center w-4 h-4 text-[9px] font-bold rounded flex-shrink-0",
                  isActive
                    ? "bg-blue-600 text-white"
                    : "bg-gray-800 text-gray-500 group-hover:bg-gray-700 group-hover:text-gray-400"
                )}>
                  {index + 1}
                </span>
              )}

              <button
                onClick={() => setActiveTabId(tab.customerId)}
                className="flex items-center gap-2"
              >
                <span className="font-semibold">{tab.customerName.split(' - ')[0]}</span>
                {condo && <span className="text-[10px] text-gray-500">{condo.lastAccess}</span>}

                {/* Unread badge */}
                {unreadCount > 0 && !isActive && (
                  <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-blue-500 text-white text-[10px] font-bold rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onCloseTab) {
                    // Direct close without confirmation for now
                    // You can add confirmation dialog if needed
                    const rect = e.currentTarget.getBoundingClientRect();
                    setCloseTabConfirmation({
                      tabName: tab.customerName,
                      index,
                      position: { x: rect.left + rect.width / 2, y: rect.bottom }
                    });
                  } else {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setCloseTabConfirmation({
                      tabName: tab.customerName,
                      index,
                      position: { x: rect.left + rect.width / 2, y: rect.bottom }
                    });
                  }
                }}
                className="ml-1 opacity-0 group-hover:opacity-100 hover:bg-gray-900 rounded p-0.5 transition-all"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// Wrap in React.memo for performance
export const CustomerTabs = React.memo(CustomerTabsComponent);
